import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  getMilestones,
  getMilestoneTemplatesByCategory,
  getMyRelationships,
  createMilestoneFromTemplate,
  completeMilestone,
  cancelMilestone
} from '../../services/api';
import HubLayout from '../common/HubLayout';
import { isAdmin } from '../../utils/permissions';

const MilestoneRegistry = () => {
  const navigate = useNavigate();
  const [milestones, setMilestones] = useState([]);
  const [templates, setTemplates] = useState({});
  const [relationships, setRelationships] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('all'); // all, pending, completed
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedPerson, setSelectedPerson] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [targetDate, setTargetDate] = useState('');
  const [fallbackDate, setFallbackDate] = useState('');
  const [addingMilestone, setAddingMilestone] = useState(false);
  const [confirmingMilestone, setConfirmingMilestone] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [milestonesData, templatesData, relationshipsData] = await Promise.all([
        getMilestones(),
        getMilestoneTemplatesByCategory(),
        getMyRelationships()
      ]);
      setMilestones(milestonesData);
      setTemplates(templatesData);

      // Extract unique people from relationships
      const peopleMap = {};
      relationshipsData.forEach(rel => {
        const otherPerson = rel.person1 || rel.person2;
        if (otherPerson && otherPerson.id) {
          peopleMap[otherPerson.id] = {
            id: otherPerson.id,
            name: `${otherPerson.first_name || ''} ${otherPerson.last_name || ''}`.trim(),
            relationship: rel.type?.name || rel.relationship_type
          };
        }
      });
      setRelationships(Object.values(peopleMap));
    } catch (err) {
      console.error('Error fetching data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleAddMilestone = async () => {
    if (!selectedPerson || !selectedTemplate) {
      return;
    }

    try {
      setAddingMilestone(true);
      await createMilestoneFromTemplate(
        selectedTemplate.id,
        selectedPerson.id,
        targetDate || null,
        fallbackDate || null
      );
      setShowAddModal(false);
      setSelectedPerson(null);
      setSelectedTemplate(null);
      setTargetDate('');
      setFallbackDate('');
      await fetchData();
    } catch (err) {
      setError(err.message);
    } finally {
      setAddingMilestone(false);
    }
  };

  // Can act on milestone if: admin, OR backend says user can complete it (parent/steward)
  const canActOnMilestone = (milestone) => {
    if (isAdmin()) return true;
    return !!milestone.can_complete;
  };

  const handleCompleteMilestone = async (milestone) => {
    try {
      await completeMilestone(milestone.id);
      setConfirmingMilestone(null);
      await fetchData();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleCancelMilestone = async (milestone) => {
    if (window.confirm('Are you sure you want to cancel this milestone?')) {
      try {
        await cancelMilestone(milestone.id);
        await fetchData();
      } catch (err) {
        setError(err.message);
      }
    }
  };

  const filteredMilestones = milestones.filter(m => {
    if (activeTab === 'pending') return m.status === 'PENDING';
    if (activeTab === 'completed') return m.status === 'COMPLETED';
    return true;
  });

  const getStatusBadge = (status) => {
    switch (status) {
      case 'PENDING':
        return <span className="px-2 py-1 text-xs font-medium rounded-full bg-yellow-100 text-yellow-800">Pending</span>;
      case 'COMPLETED':
        return <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">Completed</span>;
      case 'CANCELLED':
        return <span className="px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-800">Cancelled</span>;
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <HubLayout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </HubLayout>
    );
  }

  return (
    <HubLayout>
      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Back Link */}
        <button
          onClick={() => navigate('/timecapsule')}
          className="mb-4 text-primary hover:underline flex items-center gap-1 text-sm"
        >
          ← Back to Time Capsules
        </button>

        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Milestone Registry</h1>
            <p className="mt-2 text-gray-600">Track life milestones for your family members and trigger time capsule deliveries</p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => navigate('/letter-creation')}
              className="px-4 py-2 border-2 border-primary text-primary rounded-lg hover:bg-primary hover:text-white transition-colors flex items-center gap-2"
            >
              ✍️ Write a Letter
            </button>
            <button
              onClick={() => setShowAddModal(true)}
              className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-secondary transition-colors flex items-center gap-2"
            >
              <span className="text-lg">+</span>
              Add Milestone
            </button>
          </div>
        </div>

        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
            {error}
            <button onClick={() => setError(null)} className="ml-2 text-red-500 hover:text-red-700">
              Dismiss
            </button>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-4 mb-6 border-b border-gray-200">
          {[
            { id: 'all', label: 'All Milestones', count: milestones.length },
            { id: 'pending', label: 'Pending', count: milestones.filter(m => m.status === 'PENDING').length },
            { id: 'completed', label: 'Completed', count: milestones.filter(m => m.status === 'COMPLETED').length }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`pb-3 px-2 font-medium transition-colors relative ${
                activeTab === tab.id
                  ? 'text-primary border-b-2 border-primary'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {tab.label}
              <span className="ml-2 px-2 py-0.5 text-xs rounded-full bg-gray-100">
                {tab.count}
              </span>
            </button>
          ))}
        </div>

        {/* Milestones List */}
        {filteredMilestones.length === 0 ? (
          <div className="text-center py-12 bg-gray-50 rounded-lg">
            <div className="text-4xl mb-4">🎯</div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No milestones yet</h3>
            <p className="text-gray-600 mb-4">Create milestones to track important life events for your family members.</p>
            <button
              onClick={() => setShowAddModal(true)}
              className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-secondary"
            >
              Add Your First Milestone
            </button>
          </div>
        ) : (
          <div className="grid gap-4">
            {filteredMilestones.map(milestone => (
              <div
                key={milestone.id}
                className="bg-white rounded-lg shadow border border-gray-200 p-6 hover:shadow-md transition-shadow"
              >
                <div className="flex justify-between items-start">
                  <div className="flex items-start gap-4">
                    <div className="text-3xl">{milestone.icon}</div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">{milestone.name}</h3>
                      <p className="text-gray-600">{milestone.person_name}</p>
                      {milestone.description && (
                        <p className="text-sm text-gray-500 mt-1">{milestone.description}</p>
                      )}
                      <div className="flex gap-4 mt-2 text-sm text-gray-500">
                        {milestone.target_date && (
                          <span>Target: {new Date(milestone.target_date).toLocaleDateString()}</span>
                        )}
                        {milestone.completed_date && (
                          <span>Completed: {new Date(milestone.completed_date).toLocaleDateString()}</span>
                        )}
                        {milestone.waiting_capsules_count > 0 && (
                          <span className="text-primary font-medium">
                            {milestone.waiting_capsules_count} letter{milestone.waiting_capsules_count !== 1 ? 's' : ''} waiting
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    {getStatusBadge(milestone.status)}
                    {milestone.status === 'PENDING' && canActOnMilestone(milestone) && (
                      <div className="flex gap-2">
                        <button
                          onClick={() => setConfirmingMilestone(milestone)}
                          className="px-3 py-1 text-sm bg-green-600 text-white rounded hover:bg-green-700"
                        >
                          Complete
                        </button>
                        <button
                          onClick={() => handleCancelMilestone(milestone)}
                          className="px-3 py-1 text-sm bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
                        >
                          Cancel
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Add Milestone Modal */}
        {showAddModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-gray-200">
                <h2 className="text-xl font-bold text-gray-900">Add New Milestone</h2>
                <p className="text-gray-600 mt-1">Select a family member and milestone to track</p>
              </div>

              <div className="p-6 space-y-6">
                {/* Select Person */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Family Member
                  </label>
                  <select
                    value={selectedPerson?.id || ''}
                    onChange={(e) => {
                      const person = relationships.find(p => p.id === e.target.value);
                      setSelectedPerson(person || null);
                    }}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  >
                    <option value="">Select a family member...</option>
                    {relationships.map(person => (
                      <option key={person.id} value={person.id}>
                        {person.name} ({person.relationship})
                      </option>
                    ))}
                  </select>
                </div>

                {/* Select Template */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Milestone Type
                  </label>
                  {Object.entries(templates).map(([category, categoryTemplates]) => (
                    <div key={category} className="mb-4">
                      <h4 className="text-sm font-medium text-gray-500 mb-2">{category}</h4>
                      <div className="grid grid-cols-2 gap-2">
                        {categoryTemplates.map(template => (
                          <button
                            key={template.id}
                            onClick={() => setSelectedTemplate(template)}
                            className={`p-3 text-left border rounded-lg transition-colors ${
                              selectedTemplate?.id === template.id
                                ? 'border-primary bg-primary/5'
                                : 'border-gray-200 hover:border-gray-300'
                            }`}
                          >
                            <div className="flex items-center gap-2">
                              <span>{template.icon}</span>
                              <span className="font-medium text-sm">{template.name}</span>
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Target Date */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Expected Date (Optional)
                  </label>
                  <input
                    type="date"
                    value={targetDate}
                    onChange={(e) => setTargetDate(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  />
                  <p className="text-xs text-gray-500 mt-1">When do you expect this milestone to happen?</p>
                </div>

                {/* Fallback Date */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Fallback Date (Optional)
                  </label>
                  <input
                    type="date"
                    value={fallbackDate}
                    onChange={(e) => setFallbackDate(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  />
                  <p className="text-xs text-gray-500 mt-1">If the milestone doesn't happen, release letters on this date</p>
                </div>
              </div>

              <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
                <button
                  onClick={() => {
                    setShowAddModal(false);
                    setSelectedPerson(null);
                    setSelectedTemplate(null);
                    setTargetDate('');
                    setFallbackDate('');
                  }}
                  className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAddMilestone}
                  disabled={!selectedPerson || !selectedTemplate || addingMilestone}
                  className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-secondary disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {addingMilestone ? 'Adding...' : 'Add Milestone'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Confirm Completion Modal */}
        {confirmingMilestone && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
              <div className="text-center">
                <div className="text-4xl mb-4">{confirmingMilestone.icon}</div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  Confirm Milestone Completion
                </h3>
                <p className="text-gray-600 mb-4">
                  Are you sure <strong>{confirmingMilestone.person_name}</strong> has completed the milestone "{confirmingMilestone.name}"?
                </p>
                {confirmingMilestone.waiting_capsules_count > 0 && (
                  <div className="bg-primary/10 text-primary p-3 rounded-lg mb-4">
                    <strong>{confirmingMilestone.waiting_capsules_count}</strong> time capsule{confirmingMilestone.waiting_capsules_count !== 1 ? 's' : ''} will be unlocked and delivered!
                  </div>
                )}
              </div>
              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => setConfirmingMilestone(null)}
                  className="flex-1 px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleCompleteMilestone(confirmingMilestone)}
                  className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                >
                  Yes, Complete
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </HubLayout>
  );
};

export default MilestoneRegistry;
